import { Divider, Flex, Text } from '@chakra-ui/react';
import { useRouteQuery } from '@diamond/sol-buyer/utils';
import { HistoryOutlined } from '@mui/icons-material';
import cleanDeep from 'clean-deep';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDebounce } from 'usehooks-ts';

import {
  Autocomplete,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
  useRecentSearch,
} from '../layouts/autocomplete';

type SearchForm = {
  q: string;
  rightButton: (handleClick: () => void) => React.ReactNode;
};

export function RecipeListAutocomplete({
  q: keyword,
  rightButton,
}: SearchForm) {
  const router = useRouter();
  const routeQuery = useRouteQuery();
  const { register, reset, watch, setValue } = useForm<SearchForm>({
    defaultValues: {
      q: '',
    },
  });
  const typedKeyword = watch('q');
  const recentSearches = useRecentSearch((s) => s.searches).filter((search) =>
    search.includes(typedKeyword)
  );
  const putRecentSearch = useRecentSearch((s) => s.put);

  useEffect(() => {
    if (!keyword) {
      reset();
    }
  }, [keyword, reset, setValue]);

  const debouncedKeyword = useDebounce(typedKeyword, 500);

  const handleClick = useCallback(() => {
    router.push(
      {
        pathname: '/recipes',
        query: cleanDeep({
          ...routeQuery,
          q: typedKeyword?.trim(),
        }),
      },
      undefined,
      { shallow: true }
    );
    putRecentSearch(typedKeyword);
  }, [putRecentSearch, routeQuery, router, typedKeyword]);

  return (
    <Autocomplete
      name="q"
      keyword={debouncedKeyword}
      setKeyword={(keyword) => setValue('q', keyword)}
      onSelect={(keyword) => {
        router.push(
          {
            pathname: '/recipes',
            query: cleanDeep({
              ...routeQuery,
              q: keyword.trim(),
            }),
          },
          undefined,
          { shallow: true }
        );
        putRecentSearch(keyword);
      }}
      openMode="focus"
    >
      <Flex gap="1">
        <AutocompleteInput
          defaultValue={keyword}
          placeholder="Cari nama resep, bahan, atau brand"
          {...register('q')}
        />
        {rightButton(handleClick)}
      </Flex>
      {recentSearches.length > 0 ? (
        <AutocompleteList>
          <Text mb={1} fontSize="lg" fontWeight={500}>
            Pencarian Terakhir
          </Text>
          <Divider borderColor="gray.200" />
          {recentSearches.map((title, i) => {
            return (
              <AutocompleteItem
                key={`${title}-${i}`}
                text={title}
                icon={<HistoryOutlined fontSize="small" />}
                textProps={{ fontWeight: 'normal' }}
              />
            );
          })}
          {/* Later this will be implemented */}
          {/* <Text mt={2} mb={1} fontSize="lg" fontWeight={500}>
          Produk yang sering dicari
        </Text>
        <Divider borderColor="gray.200" />
        {['Diamond • UHT fresh milk', 'Lee Kum Kee • Oyster sauce'].map(
          (title, i) => (
            <AutocompleteItem
              key={`${title}-${i}`}
              text={title}
              textProps={{ fontWeight: 'normal' }}
            />
          )
        )}
        <Text mt={2} mb={1} fontSize="lg" fontWeight={500}>
          Resep yang sering dicari
        </Text>
        <Divider borderColor="gray.200" />
        {['Coklat kunafe', 'Burnt cheese cake'].map((title, i) => (
          <AutocompleteItem
            key={`${title}-${i}`}
            text={title}
            textProps={{ fontWeight: 'normal' }}
          />
        ))} */}
        </AutocompleteList>
      ) : null}
    </Autocomplete>
  );
}
